import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { BsModalRef } from 'ngx-bootstrap/modal';
import { tap } from 'rxjs/operators';

import { AuthenticationService } from 'app/core/authentication';
import { EmailGroup } from 'app/settings/manage-email-preferences/email-group.model';
import { ManageEmailPreferencesService } from 'app/settings/manage-email-preferences/manage-email-preferences.service';
import { AccountSettingsDataService } from 'app/shared/services/account-settings-data-service';

import { UserGroup } from './../../../settings/manage-email-preferences/user-group.model';
import { TranslationService } from 'app/core/internationalization/translation.service';

type FormGroupValue = {
  Control: FormControl;
  Description: string;
  EmailGroupId: string;
  Name: string;
  UserId: string;
};

@UntilDestroy()
@Component({
    selector: 'kh-email-notifications-modal',
    styleUrls: ['./email-notifications-modal.component.less'],
    templateUrl: './email-notifications-modal.component.html',
    standalone: false
})
export class EmailNotificationsModalComponent implements OnInit {
  @Input() public emailGroups: EmailGroup[] = [];
  @Output() public onSaveCallBack = new EventEmitter<UserGroup[]>();

  public emailGroupsForm!: FormGroup;

  public constructor(
    protected readonly bsModalRef: BsModalRef,
    protected readonly translationService: TranslationService,
    protected readonly formBuilder: FormBuilder,
    protected readonly accountSettingsDataService: AccountSettingsDataService,
    protected readonly authenticationService: AuthenticationService,
    protected readonly manageEmailPreferencesService: ManageEmailPreferencesService
  ) { }

  public ngOnInit(): void {
    const groupControls: { [key: string]: FormGroup } = {};

    this.emailGroups.forEach((group, index) => {
      groupControls[index] = this.formBuilder.group({
        Control: new FormControl(!(group.UserGroup?.Disabled ?? true)),
        Description: group.Description,
        EmailGroupId: group.EmailGroupId,
        Name: group.Name,
        UserId: group.UserId
      } as FormGroupValue);
    });

    this.emailGroupsForm = this.formBuilder.group(groupControls);
  }

  public onSave() {
    const controlNames = this.getControlNames();
    const formValues: { [key: string]: FormGroupValue } = this.emailGroupsForm.value;

    const userGroups = controlNames.map(controlName => {
      const control = formValues[controlName];

      const userGroup: UserGroup = {
        Disabled: !control.Control,
        EmailGroupId: control.EmailGroupId,
        UserId: control.UserId
      };

      return userGroup;
    });

    this.manageEmailPreferencesService.updateUserGroups(userGroups)
      .pipe(
        tap(() => {
          this.onSaveCallBack.emit(userGroups);
          this.closeModal();
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  public closeModal() {
    this.bsModalRef.hide();
  }

  public getControlNames(): string[] {
    if (this.emailGroupsForm) {
      return Object.keys(this.emailGroupsForm.controls);
    }

    return [];
  }

  public getControlValue(groupName: string, prop: string) {
    return this.emailGroupsForm.get(groupName)?.get(prop)?.value;
  }
}
